import React, { memo, startTransition, useCallback, useMemo } from "react";
import {
  isOpenBy,
  embryoTable,
  disabledFields,
  icsiDoneByEmbryoTable,
} from "./Constant";
import { useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import CustomCreatableSelect from "utils/CustomCreatableSelect";
import { EmbryologyOocyteTrackingTable } from "./EmbryologyOocyteTrackingTable";

const defaultEmbryoState = {
  gv: "",
  total_m1: "",
  total_m2: "",
  atretic: "",
  cleaved: "",
  fert: "",
  unfert: "",
  degenerated: "",
  kept_for_blastocyst: "",
  total_blastocyst: "",
};

const EmbryologyOocyteTrackingTableHandler = (props) => {
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldError,
    oocytesFieldTableData,
  } = props;

  const { embryologyData, embryologyDetails } = useSelector(
    ({ embryologyData }) => embryologyData
  );
  const { moduleList, userType, selectedLocation } = useSelector(
    ({ role }) => role
  );
  const { IvfFlowsheetList } = useSelector(({ ivfFlowSheet }) => ivfFlowSheet);

  const renderTable = useMemo(() => {
    return (
      <>
        {oocytesFieldTableData?.map((row, index) => {
          return (
            <>
              <OocyteItemListContainer
                row={row}
                key={index}
                index={index}
                errors={errors}
                touched={touched}
                handleBlur={handleBlur}
                values={values}
                tableData={values?.oocytes}
                handleChange={handleChange}
                setFieldError={setFieldError}
                setFieldValue={setFieldValue}
                IvfFlowsheetList={IvfFlowsheetList}
                embryologyDetails={embryologyDetails}
              />

              <EmbryologyOocyteTrackingTable
                i={index}
                embryoRow={row}
                moduleList={moduleList}
                tableData={values?.oocytes}
                setFieldValue={setFieldValue}
                IvfFlowsheetList={IvfFlowsheetList}
                userType={userType}
                values={values}
                embryologyData={embryologyData}
                selectedLocation={selectedLocation}
                embryologyDetails={embryologyDetails}
              />
            </>
          );
        })}
      </>
    );
  }, [
    errors,
    touched,
    values,
    userType,
    embryologyData,
    // values.oocytes,
    IvfFlowsheetList,
    embryologyDetails,
    oocytesFieldTableData,
  ]);

  return <>{renderTable}</>;
};

const OocyteItemListContainer = ({
  row,
  index,
  errors,
  touched,
  values,
  tableData,
  setFieldValue,
  setFieldError,
  IvfFlowsheetList,
  embryologyDetails,
}) => {
  const plannedCycleHasOD = embryologyDetails?.cycle_type?.includes("OD");

  const hasEmbryoFieldsError = useMemo(() => {
    if (errors?.oocytes && errors?.oocytes?.[index]) {
      const error = !!Object.keys(errors?.oocytes?.[index])?.length;
      return error;
    }
  }, [errors, index, values]);

  const disabledField = useCallback(
    (key) => {
      const cycleType = embryologyDetails?.cycle_type || "";

      if (cycleType) {
        const shouldDisableByMap = disabledFields[key]?.some((type) =>
          type === "FET+Surrogacy"
            ? cycleType.includes("FET") && cycleType.includes("Surrogacy")
            : cycleType.includes(type)
        );

        if (shouldDisableByMap) return true;
      }

      return false;
    },
    [embryologyDetails?.cycle_type]
  );

  const plannedCycleHasODorED = useMemo(() => {
    return (
      IvfFlowsheetList?.planned_cycle?.includes("OD") ||
      IvfFlowsheetList?.planned_cycle?.includes("ED")
    );
  }, [IvfFlowsheetList]);

  const addRow = useCallback(
    (m1, m2, index, data) => {
      // const newEmbryos = [];
      const updatedData = [...data];

      const embryoGroups = Object.groupBy(
        updatedData[index]["embryo"],
        ({ maturation_stage }) => maturation_stage
      );

      const stagesToAdjust = [
        { stage: "M2", count: Number(m2) },
        { stage: "M1", count: Number(m1) },
      ];

      let oocyteIndex = 1;
      const mergedEmbryoData = [];

      const getAdjustedEmbryos = (
        existing,
        desiredCount,
        stage,
        startIndex
      ) => {
        const trimmed = existing.slice(0, desiredCount);
        const missingCount = desiredCount - trimmed.length;

        const newEntries = Array.from({ length: missingCount }, () => ({
          ...embryoTable,
          maturation_stage: stage,
          hasVitrificationId: false, // Its for disable the VitrificationId field.
        }));

        const fullArray = [...trimmed, ...newEntries];

        return fullArray.map((row, i) => ({
          ...row,
          oocytes: startIndex + i,
          ...(IvfFlowsheetList?.planned_cycle?.includes("Oocytes Vitrification")
            ? { stage_of_development: row.maturation_stage }
            : {}),
        }));
      };

      for (let i = 0; i < stagesToAdjust.length; i++) {
        const { stage, count } = stagesToAdjust[i];
        const existing = embryoGroups[stage] || [];

        const adjusted = getAdjustedEmbryos(
          existing,
          count,
          stage,
          oocyteIndex
        );

        mergedEmbryoData.push(...adjusted);
        oocyteIndex += adjusted.length;
      }

      // if (m2 > 0) {
      //   for (let i = 0; i < m2; i++) {
      //     newEmbryos.push({ ...embryoTable, maturation_stage: "M2" });
      //   }
      // }
      // if (m1 > 0) {
      //   for (let i = 0; i < m1; i++) {
      //     newEmbryos.push({ ...embryoTable, maturation_stage: "M1" });
      //   }
      // }

      // const withOocytes = newEmbryos.map((item, idx) => ({
      //   ...item,
      //   oocytes: idx + 1,
      //   ...(IvfFlowsheetList?.planned_cycle?.includes("Oocytes Vitrification")
      //     ? { stage_of_development: item.maturation_stage }
      //     : {})
      // }));

      updatedData[index] = {
        ...updatedData[index],
        embryo: mergedEmbryoData,
        // gv: Math.max(
        //   (updatedData[index]["no_of_oocytes"] || 0) -
        //     (m1 + m2 + (Number(updatedData[index]["atretic"]) || 0)),
        //   0
        // )
      };

      return { updatedData };
    },
    [IvfFlowsheetList?.planned_cycle]
  );

  const ICSIDoneBySelection = useMemo(() => {
    return (
      <CustomCreatableSelect
        name={`oocytes[${index}].icsi_done_by_embryo_table`}
        value={row?.icsi_done_by_embryo_table}
        options={icsiDoneByEmbryoTable || []}
        onChange={(data) => {
          setFieldValue(`oocytes[${index}].icsi_done_by_embryo_table`, data);
        }}
        className={`custom-normal-select`}
      />

      // <Form.Item
      //   name={`icsi_done_by_embryo_table${i}`}
      //   className="custom_select mb-1 custom_select_multiple"
      //   label="ICSI Done by"
      // >
      // <Select
      //   mode="tags"
      //   allowClear={true}
      //   placeholder="Select"
      //   name={`oocytes[${index}].icsi_done_by_embryo_table`}
      //   value={row?.icsi_done_by_embryo_table}
      //   // value={embroData.icsi_done_by_embryo_table}
      //   // onChange={(value) => {
      //   //   const data = [...embryology_count];
      //   //   data[i] = {
      //   //     ...data[i],
      //   //     icsi_done_by_embryo_table: value || null
      //   //   };
      //   //   form.setFieldsValue({
      //   //     [`icsi_done_by_embryo_table${i}`]: value || null
      //   //   });
      //   //   dispatch(setEmbryology_count(data));
      //   // }}
      //   onChange={handleChange}
      //   maxCount={1}
      //   options={icsiDoneByEmbryoTable || []}
      // />
      // </Form.Item>
    );
  }, [row?.icsi_done_by_embryo_table]);

  const TotalNoOfOocytesField = useMemo(() => {
    return (
      <li
        className={`w_180 custom_normal_input ${
          errors.oocytes?.[index]?.no_of_oocytes &&
          touched?.oocytes?.[index]?.no_of_oocytes &&
          "red_row_wrapper"
        }`}
      >
        <label className="label_color">
          Total No. Of Oocytes
          {!plannedCycleHasODorED && <span className="text-danger">*</span>}
        </label>
        <input
          type="number"
          name={`oocytes[${index}].no_of_oocytes`}
          value={row.no_of_oocytes}
          onChange={(e) => {
            setFieldValue(`oocytes[${index}].no_of_oocytes`, e.target.value);
          }}
          placeholder="No. of Oocytes"
          onWheel={(e) => e.target.blur()}
          disabled={disabledField("no_of_oocytes")}
          // onBlur={(e) => {
          //   if (
          //     parseInt(row?.total_m1 || 0) +
          //       parseInt(row?.total_m2 || 0) +
          //       parseInt(row?.total_m1_used || 0) +
          //       parseInt(row?.total_m2_used || 0) <=
          //     parseInt(+e.target.value || 0)
          //   )
          //     handleEmbryoDataChange(+e.target.value, "no_of_oocytes", i);
          //   else return null;
          // }}
          // onBlur={(e) => customOnBlur(e, `oocytes[${index}].no_of_oocytes`)}
        />
        {/* {errors.oocytes?.[index]?.no_of_oocytes && (
          <div style={{ color: "red" }}>
            {errors.oocytes[index]?.no_of_oocytes}
          </div>
        )} */}
        {/* <EmbryoTableInput
          label="Total No. Of Oocytes"
          name={`no_of_oocytes${i}`}
          dependencies={[`total_m2${i}`, `total_m1${i}`]}
          rules={[
            {
              required:
                !plannedCycleHasODorED &&
                (embroData?.total_m2 > 0 || embroData?.total_m1 > 0),
              message: ""
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (plannedCycleHasODorED) {
                  return Promise.resolve();
                }

                const totalM1Value = getFieldValue(`total_m1${i}`);
                const totalM2Value = getFieldValue(`total_m2${i}`);
                const hasOtherFieldsFilled =
                  totalM1Value > 0 || totalM2Value > 0;

                const totalM1M2 =
                  parseInt(totalM1Value || 0) + parseInt(totalM2Value || 0);

                const totalM1M2Used =
                  parseInt(embroData?.total_m2_used || 0) +
                  parseInt(embroData?.total_m1_used || 0);

                const totalOocytes = parseInt(value || 0);

                if (hasOtherFieldsFilled && !value && value !== 0) {
                  return Promise.reject(new Error(""));
                }

                if (totalM1M2 + totalM1M2Used > totalOocytes) {
                  return Promise.reject(
                    new Error("Should be >= M2+M1 + UsedM2+UsedM1")
                  );
                }
                return Promise.resolve();
              }
            })
          ]}
          disabled={disabledField("no_of_oocytes")}
          placeholder="Numeric"
          value={embroData.no_of_oocytes}
          onBlur={(e) => {
            if (
              parseInt(embroData?.total_m1 || 0) +
                parseInt(embroData?.total_m2 || 0) +
                parseInt(embroData?.total_m1_used || 0) +
                parseInt(embroData?.total_m2_used || 0) <=
              parseInt(+e.target.value || 0)
            )
              handleEmbryoDataChange(+e.target.value, "no_of_oocytes", i);
            else return null;
          }}
        /> */}
      </li>
    );
  }, [
    row.no_of_oocytes,
    setFieldValue,
    disabledField,
    errors.oocytes,
    touched.oocytes,
    plannedCycleHasODorED,
  ]);

  const M2Field = useMemo(() => {
    return (
      <li className="w_180 custom_normal_input">
        <label className="label_color">M2</label>
        <input
          type="number"
          name={`oocytes[${index}].total_m2`}
          value={row?.total_m2}
          // onChange={handleChange}
          onChange={(e) => {
            setFieldValue(`oocytes[${index}].total_m2`, e.target.value);
          }}
          placeholder="Enter text"
          disabled={
            disabledField("total_m2") ||
            (!row?.no_of_oocytes && !plannedCycleHasODorED)
          }
          onWheel={(e) => e.target.blur()}
          // onChange={(e) =>
          //   handleManageState(row._id, "total_m2", e.target.value)
          // }
        />
        {errors.oocytes?.[index]?.total_m2 && (
          <div style={{ color: "red" }}>{errors.oocytes[index]?.total_m2}</div>
        )}

        {/* <EmbryoTableInput
          label="M2"
          name={`total_m2${i}`}
          rules={[
            {
              validator(_, value) {
                const remainM2 = embroData.patient_detail.ivf.remain_m2 + 1;
                if (embroData.patient_detail.ivf.remain_m2 === 0) {
                  return Promise.reject(new Error("Unavailable"));
                } else if (embroData.patient_detail.ivf.remain_m2 === null) {
                  return Promise.resolve();
                } else if (+value >= remainM2) {
                  return Promise.reject(new Error("Allow < " + remainM2));
                } else if (value > remainM2) {
                  return Promise.reject(new Error("M2 exceeds limit"));
                }
                return Promise.resolve();
              }
            }
          ]}
          placeholder="Numeric"
          disabled={
            disabledField("total_m2") ||
            (!embroData?.no_of_oocytes && !plannedCycleHasODorED)
          }
          value={embroData?.total_m2}
          onBlur={(e) => {
            const inputValue = +e.target.value;

            if (embroData?.total_m2 !== inputValue) {
              const remain_m2_value =
                embroData.patient_detail.ivf.remain_m2 === null;

              const compare_remain_m2 =
                inputValue <= embroData.patient_detail.ivf.remain_m2;

              const checkEggsAndCycleType = !(
                isOpenBy.includes(embryologyDetails?.eggs) &&
                embryologyDetails?.cycle_type?.includes("OD")
              );

              const checkEggsOwnFresh = ["Own Fresh"].includes(
                embryologyDetails?.eggs
              );

              const isValidM2 =
                compare_remain_m2 ||
                remain_m2_value ||
                checkEggsAndCycleType ||
                checkEggsOwnFresh;

              const mSumCheck =
                parseInt(embroData?.no_of_oocytes || 0) >=
                (parseInt(embroData?.total_m1 || 0) || 0) +
                  inputValue +
                  (parseInt(embroData?.total_m1_used || 0) || 0) +
                  (parseInt(embroData?.total_m2_used || 0) || 0);

              if (isValidM2 && (mSumCheck || plannedCycleHasODorED)) {
                handleEmbryoDataChange(inputValue, "total_m2", i);
              } else {
                form.validateFields([`no_of_oocytes${i}`]);
              }
            }
          }}
        /> */}
      </li>
    );
  }, [
    row?.total_m2,
    disabledField,
    errors.oocytes,
    row?.no_of_oocytes,
    plannedCycleHasODorED,
  ]);

  const UsedM2Field = useMemo(() => {
    return (
      <li className="w_180 custom_normal_input">
        <label className="label_color">Used M2</label>
        <input
          type="number"
          name={`oocytes[${index}].total_m2_used`}
          value={row?.total_m2_used}
          // onChange={handleChange}
          onChange={(e) => {
            setFieldValue(`oocytes[${index}].total_m2_used`, e.target.value);
          }}
          placeholder="M2 Used"
          onWheel={(e) => e.target.blur()}
          disabled
          // onChange={(e) =>
          //   handleManageState(
          //     row._id,
          //     "total_m2_used",
          //     e.target.value
          //   )
          // }
          // onBlur={handleBlur}
          // onBlur={(e) => customOnBlur(e, `oocytes[${index}].total_m2_used`)}
        />
        {errors.oocytes?.[index]?.total_m2_used && (
          <div style={{ color: "red" }}>
            {errors.oocytes[index]?.total_m2_used}
          </div>
        )}

        {/* <EmbryoTableInput
          label="Used M2"
          disabled
          placeholder="Used M2"
          value={embroData?.total_m2_used}
          showInput={embryologyDetails?.eggs === "Own Fresh"}
        /> */}
      </li>
    );
  }, [errors.oocytes, row?.total_m2_used]);

  const M1Field = useMemo(() => {
    return (
      <li className="w_180 custom_normal_input">
        <label className="label_color">M1</label>
        <input
          type="number"
          name={`oocytes[${index}].total_m1`}
          value={row?.total_m1}
          // onChange={handleChange}
          onChange={(e) => {
            setFieldValue(`oocytes[${index}].total_m1`, e.target.value);
          }}
          placeholder="Enter text"
          onWheel={(e) => e.target.blur()}
          disabled={
            disabledField("total_m1") ||
            (!row?.no_of_oocytes && !plannedCycleHasODorED)
          }
          // onChange={(e) =>
          //   handleManageState(row._id, "total_m1", e.target.value)
          // }
          // onBlur={(e) => customOnBlur(e, `oocytes[${index}].total_m1`)}
          // onBlur={(e) => {
          //   if (
          //     (+e.target.value <= tableData.patient_detail.ivf.remain_m1 ||
          //       tableData.patient_detail.ivf.remain_m1 === null ||
          //       !(
          //         isOpenBy.includes(embryologyDetails?.eggs) &&
          //         embryologyDetails?.cycle_type?.includes("OD")
          //       ) ||
          //       ["Own Fresh"].includes(embryologyDetails?.eggs)) &&
          //     (parseInt(tableData?.no_of_oocytes || 0) >=
          //       parseInt(tableData?.total_m2 || 0) +
          //         parseInt(+e.target.value || 0) +
          //         parseInt(tableData?.total_m1_used || 0) +
          //         parseInt(tableData?.total_m2_used || 0) ||
          //       plannedCycleHasODorED)
          //   )
          //     handleEmbryoDataChange(+e.target.value, "total_m1", i);
          //   else return null;
          // }}
        />
        {errors.oocytes?.[index]?.total_m1 && (
          <div style={{ color: "red" }}>{errors.oocytes[index]?.total_m1}</div>
        )}
        {/* <EmbryoTableInput
          label="M1"
          name={`total_m1${i}`}
          rules={[
            {
              validator(_, value) {
                const remainM1 = embroData.patient_detail.ivf.remain_m1 + 1;
                if (embroData.patient_detail.ivf.remain_m1 === 0) {
                  return Promise.reject(new Error("Unavailable"));
                } else if (embroData.patient_detail.ivf.remain_m1 === null) {
                  return Promise.resolve();
                } else if (+value >= remainM1) {
                  return Promise.reject(new Error("Allow < " + remainM1));
                } else if (value > remainM1) {
                  return Promise.reject(new Error("M1 exceeds limit"));
                }
                return Promise.resolve();
              }
            }
          ]}
          placeholder="Numeric"
          disabled={
            disabledField("total_m1") ||
            (!embroData?.no_of_oocytes && !plannedCycleHasODorED)
          }
          value={embroData?.total_m1}
          onBlur={(e) => {
            if (
              (+e.target.value <= embroData.patient_detail.ivf.remain_m1 ||
                embroData.patient_detail.ivf.remain_m1 === null ||
                !(
                  isOpenBy.includes(embryologyDetails?.eggs) &&
                  embryologyDetails?.cycle_type?.includes("OD")
                ) ||
                ["Own Fresh"].includes(embryologyDetails?.eggs)) &&
              (parseInt(embroData?.no_of_oocytes || 0) >=
                parseInt(embroData?.total_m2 || 0) +
                  parseInt(+e.target.value || 0) +
                  parseInt(embroData?.total_m1_used || 0) +
                  parseInt(embroData?.total_m2_used || 0) ||
                plannedCycleHasODorED)
            )
              handleEmbryoDataChange(+e.target.value, "total_m1", i);
            else return null;
          }}
        /> */}
      </li>
    );
  }, [
    row?.total_m1,
    disabledField,
    errors.oocytes,
    row?.no_of_oocytes,
    plannedCycleHasODorED,
  ]);

  const UsedM1Field = useMemo(() => {
    return (
      <li className="w_180 custom_normal_input">
        {/* <EmbryoTableInput
          label="Used M1"
          placeholder="Used M1"
          disabled
          value={embroData?.total_m1_used}
          showInput={embryologyDetails?.eggs === "Own Fresh"}
        /> */}
        <label className="label_color">Used M1</label>
        <input
          type="number"
          name={`oocytes[${index}].total_m1_used`}
          value={row?.total_m1_used}
          // onChange={handleChange}
          onChange={(e) => {
            setFieldValue(`oocytes[${index}].total_m1_used`, e.target.value);
          }}
          placeholder="Enter text"
          onWheel={(e) => e.target.blur()}
          disabled
          // onChange={(e) =>
          //   handleManageState(
          //     row._id,
          //     "total_m1_used",
          //     e.target.value
          //   )
          // }
          // onBlur={(e) => customOnBlur(e, `oocytes[${index}].total_m1_used`)}
        />
        {errors.oocytes?.[index]?.total_m1_used && (
          <div style={{ color: "red" }}>
            {errors.oocytes[index]?.total_m1_used}
          </div>
        )}
      </li>
    );
  }, [errors.oocytes, row?.total_m1_used]);

  const GVField = useMemo(() => {
    return (
      <li className="w_180 custom_normal_input">
        {/* <EmbryoTableInput
          label="GV"
          name={`gv${i}`}
          placeholder="Condition"
          disabled
          value={embroData?.gv}
        /> */}
        <label className="label_color">GV</label>
        <input
          type="number"
          // name={`oocytes[${index}].gv`}
          value={row?.gv}
          onChange={(e) => {
            setFieldValue(`oocytes[${index}].gv`, e.target.value);
          }}
          placeholder="Enter text"
          onWheel={(e) => e.target.blur()}
          disabled
          // onChange={(e) =>
          //   handleManageState(row._id, "gv", e.target.value)
          // }
        />
        {errors.oocytes?.[index]?.gv && (
          <div style={{ color: "red" }}>{errors.oocytes[index]?.gv}</div>
        )}
      </li>
    );
  }, [errors.oocytes, row?.gv]);

  const AtreticField = useMemo(() => {
    return (
      <li className="w_180 custom_normal_input">
        {/* <EmbryoTableInput
          label="Atretic"
          name={`atretic${i}`}
          placeholder="Condition"
          value={embroData?.atretic}
          onBlur={(e) => handleEmbryoDataChange(+e.target.value, "atretic", i)}
          disabled={disabledField("atretic")}
        /> */}
        <label className="label_color">Atretic</label>
        <input
          type="number"
          name={`oocytes[${index}].atretic`}
          value={row?.atretic}
          // onChange={handleChange}
          onChange={(e) => {
            setFieldValue(`oocytes[${index}].atretic`, e.target.value);
          }}
          placeholder="Enter text"
          onWheel={(e) => e.target.blur()}
          disabled={disabledField("atretic")}
          // onBlur={(e) => handleEmbryoDataChange(+e.target.value, "atretic", i)}
          // onChange={(e) =>
          //   handleManageState(row._id, "atretic", e.target.value)
          // }
        />
        {errors.oocytes?.[index]?.atretic && (
          <div style={{ color: "red" }}>{errors.oocytes[index]?.atretic}</div>
        )}
      </li>
    );
  }, [row?.atretic, setFieldValue, disabledField, errors.oocytes]);

  const FertField = useMemo(() => {
    return (
      <li className="w_180 custom_normal_input">
        {/* <EmbryoTableInput
          label="Fert"
          name={`fert${i}`}
          placeholder="Condition"
          value={embroData?.fert}
          disabled
        /> */}
        <label className="label_color">Fert</label>
        <input
          type="number"
          name={`oocytes[${index}].fert`}
          value={row?.fert}
          // onChange={handleChange}
          onChange={(e) => {
            setFieldValue(`oocytes[${index}].fert`, e.target.value);
          }}
          placeholder="Enter text"
          onWheel={(e) => e.target.blur()}
          disabled
          // onChange={(e) =>
          //   handleManageState(row._id, "fert", e.target.value)
          // }
        />
        {errors.oocytes?.[index]?.fert && (
          <div style={{ color: "red" }}>{errors.oocytes[index]?.fert}</div>
        )}
      </li>
    );
  }, [errors.oocytes, row?.fert]);

  const CleavedField = useMemo(() => {
    return (
      <li className="w_180 custom_normal_input">
        {/* <EmbryoTableInput
          label="Cleaved"
          name={`cleaved${i}`}
          placeholder="Condition"
          value={embroData?.cleaved}
          disabled
        /> */}
        <label className="label_color">Cleaved</label>
        <input
          type="number"
          name={`oocytes[${index}].cleaved`}
          value={row?.cleaved}
          // onChange={handleChange}
          onChange={(e) => {
            setFieldValue(`oocytes[${index}].cleaved`, e.target.value);
          }}
          placeholder="Enter text"
          onWheel={(e) => e.target.blur()}
          disabled
          // onChange={(e) =>
          //   handleManageState(row._id, "cleaved", e.target.value)
          // }
        />
        {errors.oocytes?.[index]?.cleaved && (
          <div style={{ color: "red" }}>{errors.oocytes[index]?.cleaved}</div>
        )}
      </li>
    );
  }, [errors.oocytes, row?.cleaved]);

  const UnfertField = useMemo(() => {
    return (
      <li className="w_180 custom_normal_input">
        {/* <EmbryoTableInput
          label="Unfert"
          name={`unfert${i}`}
          placeholder="Condition"
          value={embroData?.unfert}
          disabled
        /> */}
        <label className="label_color">Unfert</label>
        <input
          type="number"
          name={`oocytes[${index}].unfert`}
          value={row?.unfert}
          // onChange={handleChange}
          onChange={(e) => {
            setFieldValue(`oocytes[${index}].unfert`, e.target.value);
          }}
          placeholder="Enter text"
          onWheel={(e) => e.target.blur()}
          disabled
          // onChange={(e) =>
          //   handleManageState(row._id, "unfert", e.target.value)
          // }
        />
        {errors.oocytes?.[index]?.unfert && (
          <div style={{ color: "red" }}>{errors.oocytes[index]?.unfert}</div>
        )}
      </li>
    );
  }, [errors.oocytes, row?.unfert]);

  const DegeneratedField = useMemo(() => {
    return (
      <li className="w_150 custom_normal_input">
        {/* <EmbryoTableInput
          label="Degenerated"
          name={`degenerated${i}`}
          disabled
          placeholder="Condition"
          value={embroData?.degenerated}
        /> */}
        <label className="label_color">Degenerated</label>
        <input
          type="number"
          name={`oocytes[${index}].degenerated`}
          value={row?.degenerated}
          // onChange={handleChange}
          onChange={(e) => {
            setFieldValue(`oocytes[${index}].degenerated`, e.target.value);
          }}
          placeholder="Enter text"
          onWheel={(e) => e.target.blur()}
          disabled
          // onChange={(e) =>
          //   handleManageState(
          //     row._id,
          //     "degenerated",
          //     e.target.value
          //   )
          // }
        />
        {errors.oocytes?.[index]?.degenerated && (
          <div style={{ color: "red" }}>
            {errors.oocytes[index]?.degenerated}
          </div>
        )}
      </li>
    );
  }, [errors.oocytes, row?.degenerated]);

  const KeptForBlastocystField = useMemo(() => {
    return (
      <li className="w_180 custom_normal_input">
        {/* <EmbryoTableInput
          label="Kept for blastocyst"
          name={`kept_for_blastocyst${i}`}
          placeholder="Condition"
          disabled
          value={embroData?.kept_for_blastocyst}
        /> */}
        <label className="label_color">Kept for blastocyst</label>
        <input
          type="number"
          name={`oocytes[${index}].kept_for_blastocyst`}
          value={row?.kept_for_blastocyst}
          // onChange={handleChange}
          onChange={(e) => {
            setFieldValue(
              `oocytes[${index}].kept_for_blastocyst`,
              e.target.value
            );
          }}
          placeholder="Enter text"
          onWheel={(e) => e.target.blur()}
          disabled
          // onChange={(e) =>
          //   handleManageState(
          //     row._id,
          //     "kept_for_blastocyst",
          //     e.target.value
          //   )
          // }
        />
        {errors.oocytes?.[index]?.kept_for_blastocyst && (
          <div style={{ color: "red" }}>
            {errors.oocytes[index]?.kept_for_blastocyst}
          </div>
        )}
      </li>
    );
  }, [errors.oocytes, row?.kept_for_blastocyst]);

  const TotalBlastocystField = useMemo(() => {
    return (
      <li className="w_180 custom_normal_input">
        {/* <EmbryoTableInput
          label="Total blastocyst"
          name={`total_blastocyst${i}`}
          disabled
          placeholder="Condition"
          value={embroData?.total_blastocyst}
        /> */}
        <label className="label_color">Total blastocyst</label>

        <input
          type="number"
          name={`oocytes[${index}].total_blastocyst`}
          value={row?.total_blastocyst}
          // onChange={handleChange}
          onChange={(e) => {
            setFieldValue(`oocytes[${index}].total_blastocyst`, e.target.value);
          }}
          placeholder="Enter text"
          onWheel={(e) => e.target.blur()}
          disabled
          // onChange={(e) =>
          //   handleManageState(
          //     row._id,
          //     "total_blastocyst",
          //     e.target.value
          //   )
          // }
        />
        {errors.oocytes?.[index]?.total_blastocyst && (
          <div style={{ color: "red" }}>
            {errors.oocytes[index]?.total_blastocyst}
          </div>
        )}
      </li>
    );
  }, [errors.oocytes, row?.total_blastocyst]);

  const renderRow = useMemo(() => {
    return (
      <tr>
        <td colspan="12">
          <ul className="grid_wrapper">
            {TotalNoOfOocytesField}
            {M2Field}
            {UsedM2Field}
            {M1Field}
            {UsedM1Field}
            {GVField}
            {AtreticField}
            {FertField}
            {CleavedField}
            {UnfertField}
            {DegeneratedField}
            {KeptForBlastocystField}
            {TotalBlastocystField}
          </ul>
        </td>
      </tr>
    );
  }, [
    M1Field,
    M2Field,
    GVField,
    FertField,
    UnfertField,
    UsedM1Field,
    UsedM2Field,
    AtreticField,
    CleavedField,
    DegeneratedField,
    TotalBlastocystField,
    TotalNoOfOocytesField,
    KeptForBlastocystField,
  ]);

  const handleEmbryoDataChange = useCallback(
    (rowValues, index) => {
      if (!rowValues) return;

      const newData = [...tableData];
      const current = { ...newData[index], ...rowValues };

      // Calculate GV
      const no_oocytes = Number(current["no_of_oocytes"]) || 0;
      const m2 = Number(current["total_m2"]) || 0;
      const m1 = Number(current["total_m1"]) || 0;
      const atretic = Number(current["atretic"]) || 0;

      // Apply TotalNoOfOocytesField logic
      const shouldProceed =
        m1 + m2 + current["total_m1_used"] + current["total_m2_used"] >
          no_oocytes && !plannedCycleHasODorED;

      if (no_oocytes !== 0 && shouldProceed) {
        setFieldError([`no_of_oocytes${index}`]);
        return;
      }

      if (no_oocytes === 0) {
        Object.assign(current, defaultEmbryoState);
      } else {
        // Apply M2Field logic
        if (rowValues.hasOwnProperty("total_m2")) {
          const remain_m2 = current.patient_detail.ivf.remain_m2;
          const remain_m2_value = remain_m2 === null;
          const compare_remain_m2 = m2 <= remain_m2;

          const checkEggsAndCycleType = !(
            isOpenBy.includes(embryologyDetails?.eggs) && plannedCycleHasOD
          );

          const checkEggsOwnFresh = ["Own Fresh"].includes(
            embryologyDetails?.eggs
          );

          const isValidM2 =
            compare_remain_m2 ||
            remain_m2_value ||
            checkEggsAndCycleType ||
            checkEggsOwnFresh;

          const mSumCheck =
            parseInt(current?.no_of_oocytes || 0) >=
            (parseInt(current?.total_m1 || 0) || 0) +
              m2 +
              (parseInt(current?.total_m1_used || 0) || 0) +
              (parseInt(current?.total_m2_used || 0) || 0);

          const shouldProceed =
            isValidM2 && (mSumCheck || plannedCycleHasODorED);

          if (!shouldProceed) {
            setFieldError([`no_of_oocytes${index}`]);
            return;
          }
        }

        // Apply M1Field logic
        if (rowValues.hasOwnProperty("total_m1")) {
          const remain_m1 = current.patient_detail.ivf.remain_m1;

          const isValidM1 =
            m1 <= remain_m1 ||
            remain_m1 === null ||
            !(
              isOpenBy.includes(embryologyDetails?.eggs) && plannedCycleHasOD
            ) ||
            ["Own Fresh"].includes(embryologyDetails?.eggs);

          const mSumCheck =
            parseInt(current?.no_of_oocytes || 0) >=
            (parseInt(current?.total_m2 || 0) || 0) +
              m1 +
              (parseInt(current?.total_m1_used || 0) || 0) +
              (parseInt(current?.total_m2_used || 0) || 0);

          const shouldProceed =
            isValidM1 && (mSumCheck || plannedCycleHasODorED);

          if (!shouldProceed) {
            setFieldError([`no_of_oocytes${index}`]);
            return; // Exit early if M1 fails validation
          }
        }

        const gvCalculation = Math.max(0, no_oocytes - (m2 + m1 + atretic));

        if (!plannedCycleHasODorED) {
          current["gv"] = gvCalculation;
        }
      }

      newData[index] = current;

      const shouldUpdateRows = ["total_m2", "total_m1"].some(
        (key) => no_oocytes === 0 || current[key] != null
      );

      if (shouldUpdateRows) {
        startTransition(() => {
          const { updatedData } = addRow(
            current.total_m1,
            current.total_m2,
            index,
            newData
          );

          setFieldValue("oocytes", updatedData);
        });
      } else {
        startTransition(() => {
          setFieldValue("oocytes", newData);
        });
      }
    },
    [
      addRow,
      tableData,
      setFieldError,
      setFieldValue,
      plannedCycleHasOD,
      plannedCycleHasODorED,
      embryologyDetails?.eggs,
      // embryologyDetails?.cycle_type,
    ]
  );

  return (
    <>
      <div className="d-flex align-items-end gap-4 my-3 mt-4">
        <div className="d-flex align-items-start gap-4 ">
          <h2
            className={`${plannedCycleHasOD ? "mt-4 pt-3" : ""}`}
          >{`IVF:  ${row?.patient_detail?.ivf?.ivf_id}`}</h2>

          {plannedCycleHasOD && (
            <div className="w_170">
              <label className="label_color">ICSI Done by</label>
              {ICSIDoneBySelection}
            </div>
          )}
        </div>
        <Button
          type="button"
          className="btn_primary me-3"
          onClick={() => handleEmbryoDataChange(tableData[index], index)}
          disabled={hasEmbryoFieldsError}
        >
          Add
        </Button>
      </div>

      <table className="custom-table">
        <tbody>{renderRow}</tbody>
      </table>
    </>
  );
};

export default memo(EmbryologyOocyteTrackingTableHandler);
